/* hr-shield.js — DevTools-open lock screen detection
 *
 * KILL SWITCH: set HR_SHIELD_ENABLED = false to disable instantly, no other
 * changes needed anywhere else in the theme.
 *
 * Design constraints (do not weaken without re-reading CLAUDE.md owner request):
 *  - Desktop-only: requires viewport >=1024px wide AND a fine pointer (mouse).
 *    Mobile browsers, on-screen keyboards, and touch devices never evaluate
 *    the heuristics at all — the whole module bails out early on touch/narrow.
 *  - Size-delta (outer/inner window dimension gap) is NEVER used alone. It is
 *    only a supporting signal. The overlay only ever activates when at least
 *    one of the two independent traps below (debugger-timing or console
 *    getter) also fires in the same detection cycle. This keeps ordinary
 *    zoom, OS split-screen/snap, and window resizing from ever triggering it.
 *  - Purely a client-side visibility toggle. Never mutates or removes page
 *    content, never blocks crawlers (no user-agent sniffing, no cloaking) —
 *    view-source / SSR output is identical regardless of this script.
 */
(function () {
  'use strict';

  var HR_SHIELD_ENABLED = false;
  if (!HR_SHIELD_ENABLED) return;

  // --- Desktop gate -----------------------------------------------------
  var isFinePointer = false;
  try {
    isFinePointer = window.matchMedia('(pointer: fine)').matches;
  } catch (e) {
    isFinePointer = true; // matchMedia unsupported: assume desktop-capable
  }
  var isDesktopWidth = window.innerWidth >= 1024;

  if (!isFinePointer || !isDesktopWidth) {
    return; // never run any heuristic on mobile/tablet/touch
  }

  var overlay = document.getElementById('hr-shield-overlay');
  if (!overlay) return;

  var active = false;

  function lock() {
    if (active) return;
    active = true;
    overlay.classList.add('hr-shield--active');
    document.body.classList.add('hr-shield-lock');
  }

  function unlock() {
    if (!active) return;
    active = false;
    overlay.classList.remove('hr-shield--active');
    document.body.classList.remove('hr-shield-lock');
  }

  // --- Signal 1: window outer/inner size delta (supporting signal only) --
  var SIZE_THRESHOLD = 160; // px gap typical of a docked DevTools pane
  function sizeDeltaSignal() {
    if (!isDesktopWidth) return false; // re-check live, viewport may change
    var widthDelta = window.outerWidth - window.innerWidth;
    var heightDelta = window.outerHeight - window.innerHeight;
    return widthDelta > SIZE_THRESHOLD || heightDelta > SIZE_THRESHOLD;
  }

  // --- Signal 2: debugger-timing trap -------------------------------------
  // When DevTools is open, `debugger` statements pause execution; timing the
  // gap around one reveals an attached debugger far more reliably than size
  // alone, and is immune to zoom/split-screen.
  var debuggerSignal = false;
  function debuggerTrap() {
    var start = performance.now();
    // eslint-disable-next-line no-debugger
    debugger;
    var elapsed = performance.now() - start;
    debuggerSignal = elapsed > 100;
  }

  // --- Signal 3: console object getter trap -------------------------------
  // Logging an object whose toString/getter is invoked only when DevTools
  // actually renders/inspects it in the console panel.
  var consoleSignal = false;
  var probe = Object.defineProperty(new Image(), 'id', {
    get: function () {
      consoleSignal = true;
      return 'hr-shield-probe';
    },
  });

  function consoleTrap() {
    consoleSignal = false;
    try {
      // eslint-disable-next-line no-console
      console.log('%c', probe);
      console.clear && console.clear();
    } catch (e) {
      /* no-op */
    }
  }

  function evaluate() {
    if (!window.matchMedia('(pointer: fine)').matches || window.innerWidth < 1024) {
      unlock();
      return;
    }

    debuggerTrap();
    consoleTrap();

    // small delay lets the console getter trap resolve before we read it
    setTimeout(function () {
      var sizeSignal = sizeDeltaSignal();
      var trapSignal = debuggerSignal || consoleSignal;

      // Require the size-delta heuristic to be corroborated by a trap, OR
      // let a trap alone (debugger/console) confirm on its own — but never
      // let size-delta fire in isolation.
      if (trapSignal) {
        lock();
      } else if (!sizeSignal) {
        unlock();
      }
    }, 30);
  }

  var pollId = setInterval(evaluate, 800);
  window.addEventListener('resize', evaluate);

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      clearInterval(pollId);
    } else {
      pollId = setInterval(evaluate, 800);
      evaluate();
    }
  });

  evaluate();
})();
